import { useEffect, useMemo, useState } from "react";

import { Toast } from "../../../../../widget";
import { DEAL_KPI_OPTIONS, MAX_SELECTED_DEAL_KPIS } from "./constants";
import { DealKpiKey, DealKpiOption } from "./models";

export type PanelMode = "select" | "reorder";

export const SORTED_DEAL_KPI_OPTIONS = [...DEAL_KPI_OPTIONS].sort((a, b) => a.label.localeCompare(b.label));

interface UseSelectDealKpisPanelProps {
  show: boolean;
  selectedKpiKeys: DealKpiKey[];
  onClose: () => void;
  onSave: (selectedKpiKeys: DealKpiKey[]) => void;
}

interface UseSelectDealKpisPanelReturn {
  mode: PanelMode;
  setMode: (mode: PanelMode) => void;
  draftSelectedKpiKeys: DealKpiKey[];
  selectedOptions: DealKpiOption[];
  atMax: boolean;
  toggleKpi: (kpiKey: DealKpiKey) => void;
  onKpiDrop: (droppedOnKpiKey: DealKpiKey, draggedKpiKey: DealKpiKey) => void;
  handleSave: () => void;
  getStatusText: () => string;
}

export const useSelectDealKpisPanel = ({
  show,
  selectedKpiKeys,
  onClose,
  onSave,
}: UseSelectDealKpisPanelProps): UseSelectDealKpisPanelReturn => {
  const [draftSelectedKpiKeys, setDraftSelectedKpiKeys] = useState<DealKpiKey[]>(selectedKpiKeys);
  const [mode, setMode] = useState<PanelMode>("select");

  useEffect(() => {
    if (show) {
      setDraftSelectedKpiKeys(selectedKpiKeys);
      setMode("select");
    }
  }, [selectedKpiKeys, show]);

  const selectedOptions = useMemo(
    () =>
      draftSelectedKpiKeys
        .map((key) => DEAL_KPI_OPTIONS.find((o) => o.key === key))
        .filter((o): o is DealKpiOption => o !== undefined),
    [draftSelectedKpiKeys]
  );

  const atMax = draftSelectedKpiKeys.length >= MAX_SELECTED_DEAL_KPIS;

  const toggleKpi = (kpiKey: DealKpiKey): void => {
    if (draftSelectedKpiKeys.includes(kpiKey)) {
      setDraftSelectedKpiKeys((prev) => prev.filter((k) => k !== kpiKey));
      return;
    }
    if (atMax) return;
    setDraftSelectedKpiKeys((prev) => [...prev, kpiKey]);
  };

  const onKpiDrop = (droppedOnKpiKey: DealKpiKey, draggedKpiKey: DealKpiKey): void => {
    if (!draggedKpiKey || droppedOnKpiKey === draggedKpiKey) return;
    setDraftSelectedKpiKeys((prev) => {
      const draggedIndex = prev.indexOf(draggedKpiKey);
      const droppedOnIndex = prev.indexOf(droppedOnKpiKey);
      if (draggedIndex === -1 || droppedOnIndex === -1) return prev;
      const next = [...prev];
      next.splice(droppedOnIndex, 0, next.splice(draggedIndex, 1)[0]);
      return next;
    });
  };

  const handleSave = (): void => {
    if (draftSelectedKpiKeys.length === 0) {
      Toast.warn({ message: "Select at least one KPI" });
      return;
    }
    onSave(draftSelectedKpiKeys);
    onClose();
  };

  const getStatusText = (): string => {
    if (mode === "reorder") return "Drag to re-order - shown left to right";
    if (atMax) return "Maximum reached — deselect a KPI to swap";
    const remaining = MAX_SELECTED_DEAL_KPIS - draftSelectedKpiKeys.length;
    return `Select up to ${remaining} more`;
  };

  return {
    mode,
    setMode,
    draftSelectedKpiKeys,
    selectedOptions,
    atMax,
    toggleKpi,
    onKpiDrop,
    handleSave,
    getStatusText,
  };
};
